import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AnyAri } from '@atlassian/ari/any-ari';

import type { Maybe, SearchResult } from '../../../../common/types';
import type { ThirdPartyUser } from '../types';

import { ExternalBranchPartial } from './ExternalBranchPartial';
import { ExternalCalendarEventPartial } from './ExternalCalendarEventPartial';
import { ExternalCampaignPartial } from './ExternalCampaignPartial';
import { ExternalCasePartial } from './ExternalCasePartial';
import { ExternalCodePartial } from './ExternalCodePartial';
import { ExternalCommentPartial } from './ExternalCommentPartial';
import { ExternalCommitPartial } from './ExternalCommitPartial';
import { ExternalConversationPartial } from './ExternalConversationPartial';
import { ExternalCustomerOrgPartial } from './ExternalCustomerOrgPartial';
import { ExternalDashboardPartial } from './ExternalDashboardPartial';
import { ExternalDataTablePartial } from './ExternalDataTablePartial';
import { ExternalDealPartial } from './ExternalDealPartial';
import { ExternalDesignPartial } from './ExternalDesignPartial';
import { ExternalDocumentPartial } from './ExternalDocumentPartial';
import { ExternalLeadPartial } from './ExternalLeadPartial';
import { ExternalMessagePartial } from './ExternalMessagePartial';
import { ExternalProjectPartial } from './ExternalProjectPartial';
import { ExternalPullRequestPartial } from './ExternalPullRequestPartial';
import { ExternalRemoteLinkPartial } from './ExternalRemoteLinkPartial';
import { ExternalRepositoryPartial } from './ExternalRepositoryPartial';
import { ExternalVideoPartial } from './ExternalVideoPartial';
import { ExternalWorkItemPartial } from './ExternalWorkItemPartial';

const TEAMS_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';

const SearchThirdPartyGraphDocumentPartial = `
... on SearchResultGraphDocument {
	excerpt @skip(if: $skipFieldsFor3pInQuickFind)
	permissionLevel @skip(if: $skipFieldsFor3pInQuickFind)
	initialContributors @skip(if: $skipFieldsFor3pInQuickFind) {
		name
		id
		externalId
	}
	subtype
	integrationId
	providerId
	connectorType
	lastModifiedDate
	linkedEntities @skip(if: $skipFieldsFor3pInQuickFind) {
		id
		title
		excerpt,
		permissionLevel,
		subtype
		integrationId
		lastModifiedDate
		entity @skip(if: $skip3pEntityHydration) {
			${ExternalMessagePartial}
		}
		initialContributors {
			name
			id
			externalId
		}
		containerName @optIn(to: "SearchPerformanceImprovementChanges")
		allContributors @optIn(to: "SearchPerformanceImprovementChanges") {
			id
			accountId
			externalId
			name
			nickname
			email
			picture
			extendedProfile {
				jobTitle
				department
				organization
			}
		}
	}
	entity @skip(if: $skip3pEntityHydration) {
		${ExternalBranchPartial}
		${ExternalCalendarEventPartial}
		${ExternalCampaignPartial}
		${ExternalCasePartial}
		${ExternalLeadPartial}
		${ExternalDealPartial}
		${ExternalCustomerOrgPartial}
		${ExternalCommitPartial}
		${ExternalConversationPartial}
		${ExternalDesignPartial}
		${ExternalDashboardPartial}
		${ExternalDataTablePartial}
		${ExternalDocumentPartial}
		${ExternalMessagePartial}
		${ExternalProjectPartial}
		${ExternalPullRequestPartial}
		${ExternalRepositoryPartial}
		${ExternalVideoPartial}
		${ExternalWorkItemPartial}
		${ExternalCommentPartial}
		${ExternalRemoteLinkPartial}
	}
	entity @include(if: $includeGithubEntityHydration) {
		${ExternalPullRequestPartial}
		${ExternalRepositoryPartial}
		${ExternalWorkItemPartial}
	}
	entity @include(if: $includeLoomVideoMetadata) {
		... on ExternalVideo {
			durationInSeconds
			createdBy {
				thirdPartyUser {
					name
				}
				user {
					name
				}
			}
		}
	}
	containerName @optIn(to: "SearchPerformanceImprovementChanges") @skip(if: $skipFieldsFor3pInQuickFind)
	allContributors @optIn(to: "SearchPerformanceImprovementChanges") @skip(if: $skipFieldsFor3pInQuickFind) {
		id
		accountId
		externalId
		name
		nickname
		email
		picture
		extendedProfile {
			jobTitle
			department
			organization
		}
	}
	linkedResults @skip(if: $skipFieldsFor3pInQuickFind) {
		id
		title
		url
		type
		description
		lastModifiedDate
		category
		subtype
		integrationId
		providerId
	}
}
`;

export type SearchResultEntity =
	| ExternalBranch
	| ExternalCalendarEvent
	| ExternalCampaign
	| ExternalCase
	| ExternalLead
	| ExternalDeal
	| ExternalCustomerOrg
	| ExternalComment
	| ExternalCommit
	| ExternalConversation
	| ExternalDesign
	| ExternalDocument
	| ExternalMessage
	| ExternalProject
	| ExternalPullRequest
	| ExternalRepository
	| ExternalVideo
	| ExternalWorkItem
	| ExternalRemoteLink
	| ExternalDashboard
	| ExternalDataTable;

export type SearchResultBranchPartial = SearchResultGraphDocument<ExternalBranch>;
export type SearchResultCalendarEventPartial = SearchResultGraphDocument<ExternalCalendarEvent>;
export type SearchResultDealPartial = SearchResultGraphDocument<ExternalDeal>;
export type SearchResultCampaignPartial = SearchResultGraphDocument<ExternalCampaign>;
export type SearchResultCasePartial = SearchResultGraphDocument<ExternalCase>;
export type SearchResultLeadPartial = SearchResultGraphDocument<ExternalLead>;
export type SearchResultCustomerOrgPartial = SearchResultGraphDocument<ExternalCustomerOrg>;
export type SearchResultDashboardPartial = SearchResultGraphDocument<ExternalDashboard>;
export type SearchResultDataTablePartial = SearchResultGraphDocument<ExternalDataTable>;
export type SearchResultCommitPartial = SearchResultGraphDocument<ExternalCommit>;
export type SearchResultCommentPartial = SearchResultGraphDocument<ExternalComment>;
/**
 * @private
 * @deprecated prefer using Relay instead
 */
export type SearchResultConversationPartial = SearchResultGraphDocument<ExternalConversation>;
export type SearchResultDesignPartial = SearchResultGraphDocument<ExternalDesign>;
export type SearchResultDocumentPartial = SearchResultGraphDocument<ExternalDocument>;
/**
 * @private
 * @deprecated prefer using Relay instead
 */
export type SearchResultMessagePartial = SearchResultGraphDocument<ExternalMessage>;
export type SearchResultProjectPartial = SearchResultGraphDocument<ExternalProject>;
export type SearchResultPullRequestPartial = SearchResultGraphDocument<ExternalPullRequest>;
export type SearchResultRepositoryPartial = SearchResultGraphDocument<ExternalRepository>;
export type SearchResultSpacePartial = SearchResultGraphDocument<ExternalSpace>;
export type SearchResultVideoPartial = SearchResultGraphDocument<ExternalVideo>;
export type SearchResultWorkItemPartial = SearchResultGraphDocument<ExternalWorkItem>;
export type SearchResultRemoteLinkPartial = SearchResultGraphDocument<ExternalRemoteLink>;

export type ThirdPartyRestrictionLevel = 'restricted' | 'public' | 'private';

export interface SearchResultGraphDocument<
	EntityType extends SearchResultEntity,
> extends SearchResult {
	excerpt?: string;
	permissionLevel: Maybe<ThirdPartyRestrictionLevel>;
	subtype?: string;
	integrationId?: string;
	providerId?: string;
	lastModifiedDate?: string;
	initialContributors?: ThirdPartyUser[];
	linkedEntities?: SearchResultGraphDocument<EntityType>[];
	entity?: EntityType;
	containerName?: string;
	allContributors?: ThirdPartyUser[];
	userAssociations?: AssociationEdge<ThirdPartyUser>[];
	connectorType?: string;
}

enum AccountStatus {
	active,
	inactive,
	closed,
}

export interface ExternalUser {
	thirdPartyUser?: ThirdPartyUser;
	user?: {
		id: string;
		accountId: string;
		canonicalAccountId: string;
		accountStatus: AccountStatus;
		name: string;
		picture: string;
	};
}

interface ExternalThumbnail {
	externalUrl?: string;
}

enum ExternalDocumentCategory {
	ARCHIVE,
	AUDIO,
	CODE,
	DOCUMENT,
	FOLDER,
	FORM,
	IMAGE,
	OTHER,
	PDF,
	PRESENTATION,
	SHORTCUT,
	SPREADSHEET,
	VIDEO,
	WEB_PAGE,
}

interface ExternalDocumentType {
	category?: ExternalDocumentCategory;
	fileExtension?: string;
	iconUrl?: string;
	mimeType?: string;
}

interface ExternalLargeContent {
	asText?: string;
	mimeType?: string;
}

interface ExternalExportLink {
	mimeType?: string;
	url?: string;
}

export interface ExternalDocument {
	byteSize?: number;
	collaborators?: ExternalUser[];
	content?: ExternalLargeContent;
	createdAt?: string;
	createdBy?: ExternalUser;
	displayName?: string;
	exportLinks?: ExternalExportLink[];
	externalId?: string;
	id: string;
	lastUpdated?: string;
	lastUpdatedBy?: ExternalUser;
	owners?: ExternalUser[];
	parentId?: string;
	thumbnail?: ExternalThumbnail;
	thirdPartyId?: string;
	truncatedDisplayName?: boolean;
	type?: ExternalDocumentType;
	updateSequenceNumber?: number;
	url?: string;
	parent?: ExternalDocument;
	container?: ExternalSpace;
	reactions?: ExternalReaction[];
}

export interface ExternalReaction {
	reactionType?: string;
	total?: number;
}

export interface ExternalDesign {
	thumbnail?: { externalUrl?: string };
	thirdPartyId?: string;
}

export type ExternalVideo = {
	thumbnailUrl?: string;
	durationInSeconds?: number | null;
	embedUrl?: string;
	entityExtendedValues?: ExternalEntityExtendedValue[];
	chapters?: ExternalChapter[];
	createdBy?: ExternalUser;
	thirdPartyId?: string;
};

export type ExternalEntityExtendedValue = {
	fieldId?: string;
	fieldValue?: string;
	fieldDisplayName?: ExternalEntityExtendedValueDisplayName;
	isDisplayed?: boolean;
	dataType?: string;
};

export type ExternalEntityExtendedValueDisplayName = {
	displayName?: string;
};

export type ExternalChapter = {
	title?: string;
	startTimeInSeconds?: number;
};

export interface ExternalMessage {
	attachments?: ExternalAttachment[];
	container?: ExternalConversation;
	createdAt?: string;
	createdBy?: ExternalUser;
	description?: string;
	displayName?: string;
	externalId?: string;
	hidden?: boolean;
	id: string;
	isPinned?: boolean;
	largeContentDescription?: ExternalLargeContent;
	lastActive?: string;
	lastUpdated?: string;
	lastUpdatedBy?: ExternalUser;
	owners?: ExternalUser[];
	parentId?: string;
	reactionCount?: number;
	thumbnail?: ExternalThumbnail;
	updateSequenceNumber?: number;
	url?: string;
	associatedWith?: {
		edges?: AssociationEdge<AssociationEdgeEntity>[];
	};
	thirdPartyId?: string;
}

/**
 * @private
 * @deprecated prefer using Relay instead
 */
export type AssociationEdgeEntity = ThirdPartyUser | ExternalConversation;
/**
 * @private
 * @deprecated prefer using Relay instead
 */
export interface AssociationEdge<T extends AssociationEdgeEntity> {
	node: { entity: T; title?: string };
}

export interface ExternalAttachment {
	title?: string;
	byteSize?: number;
	mimeType?: string;
	thumbnailUrl?: string;
	url?: string;
}

export interface ExternalBranch {
	name?: string;
	container?: Pick<ExternalRepository, 'name'>;
	thirdPartyId?: string;
}

export interface ExternalComment {
	container?: Pick<ExternalSpace, 'displayName' | 'url' | 'subtype'>;
	thirdPartyId?: string;
}

export interface ExternalCommit {
	author: ExternalUser;
	container?: Pick<ExternalRepository, 'name'>;
	thirdPartyId?: string;
}

export interface ExternalPullRequest {
	title?: string;
	lastUpdate?: string;
	pullRequestId?: string;
	pullRequestStatus?: string;
	container?: Pick<ExternalRepository, 'name'>;
	thirdPartyId?: string;
}

export interface ExternalRepository {
	avatarDescription?: string;
	avatarUrl?: string;
	description?: string;
	forkOfId?: string;
	id: string;
	repositoryId?: string;
	url?: string;
	name?: string;
	thirdPartyId?: string;
}

export interface ExternalRemoteLink {
	thumbnail?: ExternalThumbnail;
	thirdPartyId?: string;
}

/**
 * @private
 * @deprecated prefer using Relay instead
 */
export interface ExternalConversation {
	__typename?: 'ExternalConversation';
	createdAt?: string;
	createdBy?: ExternalUser;
	channelId?: string;
	description?: string;
	displayName?: string;
	externalId?: string;
	id: string;
	isArchived?: boolean;
	lastUpdated?: string;
	lastUpdatedBy?: ExternalUser;
	memberCount?: number;
	members?: Array<ExternalUser | null>;
	membershipType?: ExternalMembershipType;
	owners?: Array<ExternalUser | null>;
	topic?: string;
	type?: ExternalConversationType;
	updateSequenceNumber?: number;
	url?: string;
	workspace?: string;
	associatedWith?: {
		edges?: AssociationEdge<AssociationEdgeEntity>[];
	};
	thirdPartyId?: string;
}

export interface ExternalCase {
	displayNumber?: string | null;
	status?: string | null;
	isClosed?: boolean | null;
	lastUpdated?: string | null;
	owners?: ReadonlyArray<ExternalUser | null> | null;
	createdBy?: ExternalUser | null;
	container?: { id?: string | null; displayName?: string | null } | null;
}

export interface ExternalLead {
	status?: string | null;
	lastUpdated?: string | null;
	owners?: ReadonlyArray<ExternalUser | null> | null;
	createdBy?: ExternalUser | null;
}

export interface ExternalCampaign {
	status?: string;
	startDate?: string;
	endDate?: string;
	owners: ExternalUser[];
	contributors?: ExternalUser[];
}

export interface ExternalDeal {
	contact?: ExternalUser;
	stage?: string;
	accountName?: string;
	lastActivity?: ExternalDealLastActivity;
	dealClosedAt?: string;
	opportunityAmount?: ExternalDealOpportunityAmount;
	isClosed?: boolean;
	contributors?: ExternalDealContributor[];
	thirdPartyId?: string;
	parent?: ExternalCustomerOrg;
	owners: ExternalUser[];
}

export interface ExternalCustomerOrg {
	contacts?: ExternalUser[];
	customerOrgLastActivity?: ExternalCustomerOrgLastActivity;
	lifeTimeValue?: ExternalCustomerOrgLifeTimeValue;
	websiteUrl?: string;
	industry?: string;
	contributors?: ExternalUser[];
	country?: string;
	accountType?: string;
	thirdPartyId?: string;
	displayName?: string;
	parent?: ExternalCustomerOrg;
	owners: ExternalUser[];
}

export interface ExternalDashboard {
	thirdPartyId?: string;
	elements?: ExternalDashboardElement[];
	pages?: ExternalDashboardPage[];
	owners: ExternalUser[];
	workspaceName?: string;
}

export interface ExternalDashboardElement {
	name: string;
	innerComponents?: string[];
}

export interface ExternalDashboardPage {
	name: string;
}

export interface ExternalDataTable {
	thirdPartyId?: string;
	columns: ExternalDataTableColumn[];
	owners: ExternalUser[];
	workspaceName?: string;
	dataTableType: string;
}

export interface ExternalDataTableColumn {
	name: string;
	description?: string;
	isRequired: boolean;
	columnType?: string;
}

export interface ExternalDealContributor {
	user: ExternalUser;
}

export interface ExternalCustomerOrgLifeTimeValue {
	value?: number;
	currencyCode?: string;
}

export interface ExternalCustomerOrgLastActivity {
	event: string;
	lastActivityAt: string;
}

export interface ExternalDealLastActivity {
	event: string;
	lastActivityAt: string;
}

export interface ExternalDealOpportunityAmount {
	value?: number;
	currencyCode?: string;
}

export interface ExternalAttendee {
	isOptional?: boolean;
	rsvpStatus?: ExternalAttendeeRsvpStatus;
	user?: ExternalUser;
}

export interface ExternalCalendarEvent {
	attachments?: ExternalCalendarEventAttachment[];
	attendees?: ExternalAttendee[];
	createdBy: ExternalUser;
	eventEndTime: string;
	eventStartTime: string;
	eventType?: ExternalEventType;
	location?: ExternalLocation;
	recurringEventId?: string;
	videoMeetingUrl?: string;
	thirdPartyId?: string;
}

export interface ExternalCalendarEventAttachment {
	byteSize?: number;
	mimeType?: string;
	thumbnailUrl?: string;
	title?: string;
	url?: string;
}

export interface ExternalLocation {
	name?: string;
	address?: string;
	url?: string;
	coordinates?: string;
}

enum ExternalEventType {
	APPOINTMENT = 'appointment',
	BIRTHDAY = 'birthday',
	DEFAULT = 'default',
	EVENT = 'event',
	FOCUS_TIME = 'focus_time',
	OUT_OF_OFFICE = 'out_of_office',
	REMINDER = 'reminder',
	TASK = 'task',
	WORKING_LOCATION = 'working_location',
}

enum ExternalAttendeeRsvpStatus {
	ACCEPTED = 'accepted',
	DECLINED = 'declined',
	NOT_RESPONDED = 'not_responded',
	OTHER = 'other',
	TENATIVELY_ACCEPTED = 'tenatively_accepted',
}

export interface ExternalProject {
	assignee?: ExternalUser;
	attachments?: ExternalWorkItemAttachment[];
	container?: Pick<ExternalSpace, 'displayName' | 'url' | 'subtype'>;
	createdBy: ExternalUser;
	dueDate?: string;
	key?: string;
	status: string;
	thirdPartyId?: string;
}

export interface ExternalWorkItem {
	assignee?: ExternalUser;
	attachments?: ExternalWorkItemAttachment[];
	collaborators?: ExternalUser[];
	container?: Pick<ExternalSpace, 'displayName' | 'url' | 'subtype'>;
	createdBy: ExternalUser;
	dueDate?: string;
	exceedsMaxCollaborators?: boolean;
	workItemProject?: ExternalWorkItemProject;
	status: string;
	subtype: ExternalWorkItemType;
	team: string;
	thirdPartyId?: string;
	provider?: { name?: string | null } | null;
}

interface ExternalWorkItemProject {
	id: string;
	name: string;
}

interface ExternalWorkItemAttachment {
	byteSize?: number;
	mimeType?: string;
	thumbnailUrl?: string;
	title?: string;
	url?: string;
}

enum ExternalWorkItemType {
	TASK = 'task',
	DEFAULT_TASK = 'default_task',
	WORK_ITEM = 'work_item',
	EPIC = 'epic',
	BUG = 'bug',
	STORY = 'story',
	MILESTONE = 'milestone',
	SECTION = 'section',
	APPROVAL = 'approval',
	PROBLEM = 'problem',
	INCIDENT = 'incident',
	QUESTION = 'question',
	ISSUE = 'issue',
	TICKET = 'ticket',
	REQUESTED_ITEM = 'requested_item',
	CATALOG_ITEM = 'catalog_item',
	OTHER = 'other',
}

enum ExternalMembershipType {
	PRIVATE = 'PRIVATE',
	PUBLIC = 'PUBLIC',
	SHARED = 'SHARED',
}

enum ExternalConversationType {
	CHANNEL = 'CHANNEL',
	DIRECT_MESSAGE = 'DIRECT_MESSAGE',
	GROUP_DIRECT_MESSAGE = 'GROUP_DIRECT_MESSAGE',
}

export enum ResourceType {
	BRANCH = 'branch',
	CALENDAR_EVENT = 'calendar-event',
	CAMPAIGN = 'campaign',
	CASE = 'case',
	LEAD = 'lead',
	DEAL = 'deal',
	CUSTOMER_ORG = 'customer-org',
	CUSTOMER_CONTACT = 'customer-contact',
	COMMENT = 'comment',
	COMMIT = 'commit',
	CONVERSATION = 'conversation',
	DESIGN = 'design',
	DOCUMENT = 'document',
	MESSAGE = 'message',
	PROJECT = 'project',
	PULL_REQUEST = 'pull-request',
	REPOSITORY = 'repository',
	VIDEO = 'video',
	WORK_ITEM = 'work-item',
	SPACE = 'space',
	REMOTE_LINK = 'remote-link',
	DASHBOARD = 'dashboard',
	DATA_TABLE = 'data-table',
}

export interface ExternalSpace {
	associatedWith?: {
		edges?: AssociationEdge<AssociationEdgeEntity>[];
	};
	createdAt?: string;
	createdBy?: ExternalUser;
	description?: string;
	displayName: string;
	externalId?: string;
	icon?: ExternalIcon;
	id: string;
	key: string;
	labels?: string[];
	lastUpdated: string;
	lastUpdatedBy?: string;
	spaceType: string;
	subtype?: string;
	updateSequenceNumber: number;
	url: string;
}

export type ExternalIcon = {
	height?: number;
	isDefault?: boolean;
	url?: string;
	width?: number;
};

export interface ExternalCodeRepository {
	name?: string;
}

export interface SearchResultGraphCode {
	__typename: 'SearchResultGraphCode';
	// Base SearchResult fields provided by the outer query's shared `... on SearchResult`
	// fragment plumbing.
	id: string;
	title?: string;
	url: string;
	lastModifiedDate?: string;
	// Code-specific fields requested by ExternalCodePartial.
	path?: string;
	repository?: ExternalCodeRepository;
}

export const getGraphPartials = (): string => {
	// One inline fragment per top-level 3P SearchResult subtype. Code results are a
	// distinct GraphQL type (SearchResultGraphCode); all other 3P documents share
	// SearchResultGraphDocument and discriminate via `entity { ... on ExternalX }`.
	// SearchResultGraphCode is gated to limit blast radius until the experiment ships.
	const partials: string[] = [SearchThirdPartyGraphDocumentPartial];
	if (fg('rovo_github_code_search')) {
		partials.push(ExternalCodePartial);
	}
	return partials.join('\n');
};

/**
 * Compatability for Relay: we define the interface we require for helpers that can be fulfilled by results from fetch or from Relay
 */
export interface I3PSearchResultPartial {
	id: string;
	__typename: string;
}

export const isSearchResultGraphEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultGraphDocument<SearchResultEntity> =>
	partial.__typename === 'SearchResultGraphDocument';

export const isConversationEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultConversationPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.CONVERSATION;
};

export const isDesignEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultDesignPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.DESIGN;
};

export const isMessageEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultMessagePartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.MESSAGE;
};

export const isVideoEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultVideoPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.VIDEO;
};

export const isBranchEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultBranchPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.BRANCH;
};

export const isCommitEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultCommitPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.COMMIT;
};

export const isCommentEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultCommentPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.COMMENT;
};

export const isPullRequestEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultPullRequestPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.PULL_REQUEST;
};

export const isRepositoryEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultRepositoryPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.REPOSITORY;
};

export const isDocumentEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultDocumentPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.DOCUMENT;
};

export const isProjectEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultProjectPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.PROJECT;
};

export const isCalendarEventEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultCalendarEventPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.CALENDAR_EVENT;
};

export const isDealEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultDealPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.DEAL;
};

export const isCaseEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultCasePartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.CASE;
};

export const isLeadEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultLeadPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.LEAD;
};

export const isCampaignEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultCampaignPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.CAMPAIGN;
};

export const isCustomerOrgEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultCustomerOrgPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.CUSTOMER_ORG;
};

export const isTeamsMessage = (partial: I3PSearchResultPartial): boolean =>
	isMessageEntity(partial) && partial.integrationId === TEAMS_INTEGRATION_ARI;

export const isWorkItemEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultWorkItemPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.WORK_ITEM;
};

export const isDashboardEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultDashboardPartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.DASHBOARD;
};

export const isCodeResult = (
	partial: I3PSearchResultPartial | SearchResultGraphCode,
): partial is SearchResultGraphCode => partial.__typename === 'SearchResultGraphCode';

export const isDataTableEntity = (
	partial: I3PSearchResultPartial,
): partial is SearchResultDataTablePartial => {
	const { resourceType } = AnyAri.parse(partial.id);
	return resourceType === ResourceType.DATA_TABLE;
};
